import { Injectable, Optional, SkipSelf } from '@angular/core';
import { BehaviorSubject, Observable, throwError, of, timer, firstValueFrom } from 'rxjs';
import { tap, catchError, switchMap, map } from 'rxjs/operators';
import { AuthService as ApiAuthService } from '@marina/api-client';
import type {
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  InitResponse,
  SessionPolicy,
  UserDto,
  MarinaInfo,
  RequestPasswordResetRequest,
  ConfirmPasswordResetRequest,
  ChangePasswordRequest,
  SendActivationLinkRequest,
  ActivateAccountRequest,
  AdminResetPasswordRequest,
} from '@marina/models';
import { isPublicAuthFlowUrl } from '../utils/auth-endpoints.util';
import { isTerminalAuthFailure } from '../utils/http-status.util';
import { AuthSessionStateService } from './auth-session-state.service';
import { TokenRefreshCoordinator } from './token-refresh-coordinator.service';
import { AuthSessionTerminatorService } from './auth-session-terminator.service';
import { UserActivityService } from './user-activity.service';

/**
 * How confident we are about who the user is.
 *
 * `unresolved` is the state that did not exist before and had to: we hold what looks like a valid
 * session but could not confirm it, because the server answered 5xx or the network dropped. The app
 * previously had no way to say that — `init()` cleared auth on *any* error and the shell navigated to
 * the login page, so a page reload during a brief outage logged the user out of a session that was
 * never in question.
 */
export type AuthState = 'unknown' | 'authenticated' | 'unresolved' | 'anonymous';

/**
 * Treat the access token as spent this long before it actually expires, so a connection is never
 * opened with a credential that dies mid-handshake. Matches AuthInterceptor's own margin.
 */
const CREDENTIAL_REFRESH_MARGIN_MS = 30_000;

/**
 * Authentication service wrapper
 * Provides higher-level authentication operations and state management.
 * Caches Init API response for authorization checks.
 */
@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private currentUserSubject = new BehaviorSubject<UserDto | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();

  // Cached Init API response
  private initDataSubject = new BehaviorSubject<InitResponse | null>(null);
  public initData$ = this.initDataSubject.asObservable();

  // Current marina selection
  private currentMarinaIdSubject = new BehaviorSubject<string | null>(null);
  public currentMarinaId$ = this.currentMarinaIdSubject.asObservable();

  // Store token expiration times (from login/refresh response - enables proactive refresh)
  private accessTokenExpiresAt: Date | null = null;
  private refreshTokenExpiresAt: Date | null = null;
  // Time at which the access token was received — used to derive token lifetime for proactive refresh
  private accessTokenIssuedAt: Date | null = null;
  /** True when Angular built this on top of an existing root instance — see the constructor. */
  private readonly isDuplicateInstance: boolean;

  // Idle policy of the current session (Remember Me on/off), as told by the API. SessionTimeoutService
  // follows this instead of a hardcoded hour.
  private sessionPolicySubject = new BehaviorSubject<SessionPolicy | null>(null);
  public sessionPolicy$ = this.sessionPolicySubject.asObservable();

  private authStateSubject = new BehaviorSubject<AuthState>('unknown');
  public authState$ = this.authStateSubject.asObservable();

  constructor(
    private apiAuthService: ApiAuthService,
    private sessionState: AuthSessionStateService,
    private coordinator: TokenRefreshCoordinator,
    private terminator: AuthSessionTerminatorService,
    private userActivity: UserActivityService,
    // Null for the root instance. Set when a lazy-loaded module has listed AuthService in its own
    // `providers` array and Angular is building a second copy on top of the root one.
    @Optional() @SkipSelf() parent: AuthService | null = null,
  ) {
    // There must be exactly one of these. Everything that reacts to a session — the refresh timer,
    // the interceptor, the guards, the terminator — is root-scoped and reads *this* instance's
    // fields. A second instance would register its own refresh handler with the shared coordinator,
    // write each new expiry into its own fields, and leave the root instance's expiry where it was;
    // the timer then read a token that never advanced and refreshed again, and again, all night —
    // about five thousand rotations of a perfectly good customer session (plan §8.12). Nothing here
    // can stop Angular constructing the duplicate, but it must not take over the singletons.
    this.isDuplicateInstance = parent !== null;
    if (this.isDuplicateInstance) {
      console.error(
        '[AuthService] A second AuthService instance was created. Remove AuthService from that ' +
        "module's `providers` — it is providedIn: 'root' and must be a singleton. This instance " +
        'will not register with the refresh coordinator, the session terminator or the activity ' +
        'service; the root instance keeps them.');
    }

    // §2.4 — Do NOT restore marina from localStorage here. The marina is only valid
    // once we know who the authenticated user is; restoring it before that would
    // cause the interceptor to send X-Marina-Id on the login screen (and potentially
    // expose a previous user's marina selection on a shared browser).
    // Marina is restored only inside initializeFromCache(), which runs after we
    // have confirmed a cached, validated authentication context.
    
    // Try to initialize from cached data if available.
    // restoreTokenExpirations() is only called when initializeFromCache() confirms
    // there is a prior authenticated session (cachedInitData present). On the login
    // screen, 2FA page, or any unauthenticated route there is nothing to restore and
    // the token-expiration localStorage reads are skipped entirely.
    const hadCachedSession = this.initializeFromCache();
    if (hadCachedSession) {
      this.restoreTokenExpirations();
    }
    
    // Listen for storage events from other tabs (cross-tab token refresh synchronization)
    this.setupStorageEventListener();

    // Only the root instance owns the singletons below. A duplicate (see the guard above) skips all
    // three registrations, so whatever it does stays inside its own fields.
    if (!this.isDuplicateInstance) {
      // The coordinator owns *when* a refresh happens and who waits for it; this service owns *how*.
      // Passing the call in as a handler keeps the coordinator below this service in the graph, so
      // neither has to inject the other.
      this.coordinator.registerRefreshHandler(() => this.performRefresh());

      // Likewise for ending a session: the terminator decides, this service does the clearing and
      // makes the server call.
      this.terminator.registerHandlers(
        () => this.clearAuth(),
        () => firstValueFrom(this.apiAuthService.logout()).then(() => undefined),
      );

      // The server hears about a visible tab, not traffic (see UserActivityService). This is how
      // customer pages that make no HTTP after load keep the idle window from closing.
      this.userActivity.registerHeartbeatSender(
        () => firstValueFrom(this.apiAuthService.recordActivity()).then(() => undefined));
    }

    if (hadCachedSession) {
      this.userActivity.start();
    }

    this.authStateSubject.next(hadCachedSession ? 'authenticated' : 'anonymous');
  }

  get authState(): AuthState {
    return this.authStateSubject.value;
  }

  /**
   * Resolve once the auth cookies are current, refreshing them first if they are spent.
   *
   * For anything going through `HttpClient` this is already handled — `AuthInterceptor` refreshes
   * before it sends. SignalR is the exception: it negotiates over its own HTTP client, so no
   * interceptor sees it and a hub connection made with a lapsed cookie simply fails. Reopening a tab
   * after the access token expired produced a 401 from every hub and a "Connection lost" dialog that
   * cleared itself seconds later, once an ordinary API call did the refresh.
   *
   * Never throws: a caller that cannot connect has its own handling, and this must not turn a hub
   * problem into an unhandled rejection.
   */
  async ensureFreshCredentials(): Promise<void> {
    // Never signed in, or already signed out — nothing to refresh, and asking would be wrong.
    if (this.terminator.isSessionOver()) {
      return;
    }
    if (!this.accessTokenExpiresAt && !this.refreshTokenExpiresAt) {
      return;
    }

    const spent = !this.accessTokenExpiresAt
      || this.accessTokenExpiresAt.getTime() - CREDENTIAL_REFRESH_MARGIN_MS <= Date.now();
    if (!spent) {
      return;
    }

    try {
      await firstValueFrom(this.coordinator.getOrStart('interceptor', this.sessionState.sessionId));
    } catch {
      // The refresh failed. The caller will fail too, and the ordinary request path will end the
      // session if that is what the failure meant — this is not the place to decide it.
    }
  }

  /** Records that we hold a session we could not confirm — see {@link AuthState}. */
  markSessionUnresolved(): void {
    if (this.authStateSubject.value !== 'unresolved') {
      this.authStateSubject.next('unresolved');
    }
  }

  /**
   * Get access token expiration time
   */
  getAccessTokenExpiresAt(): Date | null {
    return this.accessTokenExpiresAt;
  }

  /**
   * Get the time at which the current access token was received (approximate issued-at).
   * Used by TokenRefreshTimerService to derive the full token lifetime so it can fire
   * a refresh at exactly 20% remaining rather than at 80% of remaining time.
   */
  getAccessTokenIssuedAt(): Date | null {
    return this.accessTokenIssuedAt;
  }

  /**
   * Get refresh token expiration time
   */
  getRefreshTokenExpiresAt(): Date | null {
    return this.refreshTokenExpiresAt;
  }

  /**
   * Idle policy of the current session, or null when nothing has been reported yet.
   */
  getSessionPolicy(): SessionPolicy | null {
    return this.sessionPolicySubject.value;
  }

  /**
   * Record the session policy carried on an auth response. Only responses that established or refreshed
   * a session carry it; anything else (2FA-required login, failed login) leaves the current value alone.
   */
  private applySessionPolicy(
    response: { sessionIdleTimeoutMinutes?: number | null; rememberMe?: boolean | null } | null | undefined
  ): void {
    if (!response) {
      return;
    }
    const hasIdle = response.sessionIdleTimeoutMinutes !== undefined && response.sessionIdleTimeoutMinutes !== null;
    const hasRemember = response.rememberMe !== undefined && response.rememberMe !== null;
    if (!hasIdle && !hasRemember) {
      return;
    }
    const previous = this.sessionPolicySubject.value;
    const policy: SessionPolicy = {
      idleTimeoutMinutes: hasIdle ? response.sessionIdleTimeoutMinutes! : previous?.idleTimeoutMinutes ?? null,
      rememberMe: hasRemember ? !!response.rememberMe : previous?.rememberMe ?? false,
    };

    // Only publish a policy that actually differs. Login, init, refresh and 2FA completion all carry
    // the same policy, and every emission re-arms the idle timer and rewrites localStorage — so an
    // ordinary login announced "Idle logout armed" two or three times for one unchanged rule, which
    // reads like something is being reconfigured when nothing is.
    if (previous
        && previous.idleTimeoutMinutes === policy.idleTimeoutMinutes
        && previous.rememberMe === policy.rememberMe) {
      return;
    }

    this.sessionPolicySubject.next(policy);
    try {
      localStorage.setItem('sessionPolicy', JSON.stringify(policy));
    } catch (e) {
      console.warn('[AuthService] Failed to save session policy to localStorage', e);
    }
  }

  private restoreSessionPolicy(): void {
    try {
      const raw = localStorage.getItem('sessionPolicy');
      if (!raw) {
        return;
      }
      const parsed = JSON.parse(raw) as Partial<SessionPolicy>;
      this.sessionPolicySubject.next({
        idleTimeoutMinutes: typeof parsed.idleTimeoutMinutes === 'number' ? parsed.idleTimeoutMinutes : null,
        rememberMe: !!parsed.rememberMe,
      });
    } catch {
      // Ignore storage/parse errors - the next init() response re-populates it.
    }
  }

  /**
   * Get current user value
   */
  get currentUserValue(): UserDto | null {
    return this.currentUserSubject.value;
  }

  /**
   * Get cached Init API data
   */
  get initDataValue(): InitResponse | null {
    return this.initDataSubject.value;
  }

  /**
   * Get current marina ID
   */
  get currentMarinaIdValue(): string | null {
    return this.currentMarinaIdSubject.value;
  }

  /**
   * Check if user is authenticated
   * Note: Since tokens are in httpOnly cookies, we check if we have cached user data
   */
  isAuthenticated(): boolean {
    return this.currentUserSubject.value !== null;
  }

  /**
   * Login user
   * 1. Calls login endpoint (backend sets httpOnly cookies)
   * 2. Calls init endpoint to get user context (marinas, roles, system admin)
   * 3. Caches Init API response
   * @param deviceToken Optional device token for remembered devices
   * @param deviceFingerprint Optional device fingerprint
   */
  login(
    email: string,
    password: string,
    rememberMe?: boolean,
    deviceToken?: string | null,
    deviceFingerprint?: string | null
  ): Observable<LoginResponse> {
    console.log('[AuthService] Login: Starting login for', email);
    const request: LoginRequest = { email, password, rememberMe, deviceToken };
    let loginEstablishedSession = false;
    return this.apiAuthService.login(request, deviceToken, deviceFingerprint).pipe(
      tap((loginResponse) => {
        loginEstablishedSession = this.didLoginEstablishSession(loginResponse);
        console.log('[AuthService] Login: Login API response', {
          userId: loginResponse.user.id,
          email: loginResponse.user.email,
          isSystemAdmin: loginResponse.isSystemAdmin,
          requiresTwoFactor: loginResponse.requiresTwoFactor,
          twoFactorSessionId: loginResponse.twoFactorSessionId,
        });

        // Store token expiration before publishing the user — AppComponent's auth
        // transition reads getAccessTokenExpiresAt() synchronously when currentUser emits.
        if (loginResponse.accessTokenExpiresAt) {
          this.accessTokenExpiresAt = new Date(loginResponse.accessTokenExpiresAt);
          this.saveTokenExpiration('accessTokenExpiresAt', this.accessTokenExpiresAt);
          this.recordAccessTokenIssuedAt();
          console.log('[AuthService] Login: Stored access token expiration:', this.accessTokenExpiresAt);
        }
        if (loginResponse.refreshTokenExpiresAt) {
          this.refreshTokenExpiresAt = new Date(loginResponse.refreshTokenExpiresAt);
          this.saveTokenExpiration('refreshTokenExpiresAt', this.refreshTokenExpiresAt);
          console.log('[AuthService] Login: Stored refresh token expiration:', this.refreshTokenExpiresAt);
        }
        this.applySessionPolicy(loginResponse);

        // Publish the login user immediately so UI (dashboard greeting, topbar) cannot
        // keep showing the previous account while init() is in flight after an account switch.
        if (
          loginEstablishedSession &&
          !loginResponse.requiresTwoFactor &&
          loginResponse.user?.id
        ) {
          this.beginSession(loginResponse as { sessionId?: string | null });
          this.currentUserSubject.next(loginResponse.user);
        }
      }),
      switchMap((loginResponse) => {
        // Wrong password / lockout responses are HTTP 200 with failure fields — do not call init().
        if (this.isFailedCredentialLogin(loginResponse)) {
          return of(loginResponse);
        }

        // If 2FA is required, return the response without calling init()
        // The init() will be called after 2FA verification is complete
        if (loginResponse.requiresTwoFactor) {
          console.log('[AuthService] Login: 2FA required, skipping init API call');
          return of(loginResponse);
        }

        // Call Init API to get marinas and roles (only if 2FA is not required)
        console.log('[AuthService] Login: Calling init API...');
        return this.init().pipe(
          switchMap((initResponse) => {
            console.log('[AuthService] Login: Init API successful', {
              accessibleMarinas: initResponse.accessibleMarinas.length,
              rolesPerMarina: Object.keys(initResponse.rolesPerMarina).length,
            });
            
            // Portal tenant: prefer customer's marina (pure client or Client-scope hybrid).
            this.applyPortalCustomerMarinaFromInit(initResponse);
            if (!this.shouldPreferPortalCustomerMarina(initResponse) && initResponse.accessibleMarinas.length > 0) {
              // For staff/admin: auto-select marina (single or multiple - always auto-select first/primary)
              // This ensures marina is set immediately, no modal needed
              this.applyMarinaSelectionForStaff(initResponse);
            }
            
            // Return login response
            return of(loginResponse);
          })
        );
      }),
      catchError((error) => {
        console.error('[AuthService] Login: Error occurred', error);
        this.clearAuth();

        // Only show the cookie/session message when credentials were accepted and cookies
        // were expected, but the subsequent init call failed with 401.
        if (loginEstablishedSession) {
          const statusCode = error?.statusCode ?? error?.status;
          const message = (error?.message || '').toLowerCase();
          const isUnauthorized = statusCode === 401 || message.includes('unauthorized');

          if (isUnauthorized) {
            return throwError(
              () =>
                new Error(
                  'Your sign-in was accepted, but this browser could not keep you logged in. ' +
                    'Use HTTPS, allow cookies for this site, and try again. ' +
                    'If the problem continues, contact support.'
                )
            );
          }
        }

        return throwError(() => error);
      })
    );
  }

  /**
   * Complete login after 2FA verification
   * Called after successful 2FA verification to complete the login flow
   * @param accessToken Access token from 2FA verification response
   * @param refreshToken Refresh token from 2FA verification response (optional)
   * @param accessTokenExpiresAt Access token expiration (optional, when provided by backend)
   * @param refreshTokenExpiresAt Refresh token expiration (optional, when provided by backend)
   */
  completeLoginAfterTwoFactor(
    _accessToken?: string | null,
    _refreshToken?: string | null,
    accessTokenExpiresAt?: string | null,
    refreshTokenExpiresAt?: string | null,
    sessionPolicy?: { sessionIdleTimeoutMinutes?: number | null; rememberMe?: boolean | null } | null
  ): Observable<InitResponse> {
    // Store token expiration times when provided (enables proactive refresh).
    // Captured here so we can re-apply them on retry: init()'s catchError calls
    // clearAuth() which wipes them in-memory, but the httpOnly cookies set by
    // verify-2fa remain in the browser and the next init() attempt can succeed.
    const accessExpiresIso = accessTokenExpiresAt ?? null;
    const refreshExpiresIso = refreshTokenExpiresAt ?? null;

    const applyTokenExpirations = (): void => {
      if (accessExpiresIso) {
        this.accessTokenExpiresAt = new Date(accessExpiresIso);
        this.saveTokenExpiration('accessTokenExpiresAt', this.accessTokenExpiresAt);
        this.recordAccessTokenIssuedAt();
      }
      if (refreshExpiresIso) {
        this.refreshTokenExpiresAt = new Date(refreshExpiresIso);
        this.saveTokenExpiration('refreshTokenExpiresAt', this.refreshTokenExpiresAt);
      }
      this.applySessionPolicy(sessionPolicy);
    };

    applyTokenExpirations();

    // The server has just written cookies for a new login, so a new session begins here — exactly as
    // it does in login() for the password-only path. Without this, a 2FA (or activation, or reset)
    // login never re-armed the session terminator: after any earlier session end the "session is
    // over" gate stayed shut, and the init() below was refused client-side before it was sent.
    this.beginSession(null);

    // One short retry on init() failure to absorb the cookie-propagation race
    // some browsers exhibit on a fresh device immediately after verify-2fa
    // (cookies set in the response, not yet attached to the next request).
    return this.init().pipe(
      catchError(() =>
        timer(400).pipe(
          switchMap(() => {
            applyTokenExpirations();
            return this.init();
          })
        )
      )
    );
  }

  /**
   * Init API - Returns complete user context including accessible marinas and per-marina roles.
   * Called immediately after login to populate frontend state.
   * Caches the response for authorization checks.
   */
  init(): Observable<InitResponse> {
    console.log('[AuthService] Init: Calling init API...');
    return this.apiAuthService.init().pipe(
      switchMap((response) => {
        console.log('[AuthService] Init: Success', {
          userId: response.user.id,
          accessibleMarinas: response.accessibleMarinas.length,
          rolesPerMarina: Object.keys(response.rolesPerMarina).length,
          isSystemAdmin: response.isSystemAdmin,
          customerMarinaId: response.customerMarinaId,
        });
        // Cache Init API response
        this.initDataSubject.next(response);
        this.applySessionPolicy(response);
        this.currentUserSubject.next(response.user);
        this.ensureSessionIdentity(response as { sessionId?: string | null });

        // Set marina ID from InitResponse
        const preferPortal = this.shouldPreferPortalCustomerMarina(response);

        if (preferPortal) {
          const currentMarinaId = this.getCurrentMarinaId();
          if (currentMarinaId !== response.customerMarinaId) {
            console.log(
              '[AuthService] Init: Setting marina from portal customer context. Old:',
              currentMarinaId,
              'New:',
              response.customerMarinaId
            );
            this.setCurrentMarina(response.customerMarinaId!);
          } else {
            console.log('[AuthService] Init: Marina matches portal customer context:', currentMarinaId);
            this.setCurrentMarina(response.customerMarinaId!);
          }
        } else if (response.accessibleMarinas.length > 0) {
          const currentMarinaId = this.getCurrentMarinaId();

          if (currentMarinaId) {
            const isAccessible = response.accessibleMarinas.some((m) => m.id === currentMarinaId);
            if (!isAccessible) {
              console.warn('[AuthService] Init: Current marina is no longer accessible, clearing and re-selecting');
              this.currentMarinaIdSubject.next(null);
              this.clearStoredMarina();
            } else {
              this.setCurrentMarina(currentMarinaId);
              console.log('[AuthService] Init: Current marina is valid:', currentMarinaId);
            }
          }

          if (!this.getCurrentMarinaId()) {
            console.log('[AuthService] Init: Applying marina selection for staff/admin');
            this.applyMarinaSelectionForStaff(response);
          }
        }

        // Store in localStorage for persistence across page refreshes and tabs
        // This ensures new tabs can access initData immediately without calling init() again
        try {
          localStorage.setItem('marina_init_data', JSON.stringify(response));
          console.log('[AuthService] Init: Stored initData in localStorage (shared across tabs)');
        } catch (e) {
          console.error('[AuthService] Init: Failed to store in localStorage', e);
          // Ignore storage errors (e.g., in private browsing)
        }
        
        // Keep the logged-in ApplicationUser display name (authorized user / owner login).
        // Do not overwrite with Customer profile names — those may be the Boat Club owner, not the current user.
        return of(response);
      }),
      catchError((error) => {
        console.error('[AuthService] Init: Error occurred', {
          error,
          message: error?.message,
          status: error?.status ?? error?.statusCode,
          reason: error?.reason,
          traceId: error?.traceId,
          url: error?.url,
        });

        // Deliberately does not clear auth. A terminal failure (401/403, or the server saying the
        // session ended) has already been handled by the interceptor, which ran the terminator; a
        // temporary one — 5xx, or no response at all — is not evidence of anything about the session,
        // and clearing here is what turned a page reload during a brief outage into a logout. The
        // caller decides what to show; `authState` says which case this is.
        if (this.isTerminalAuthError(error)) {
          this.authStateSubject.next('anonymous');
        } else if (this.currentUserSubject.value || this.initDataSubject.value) {
          this.markSessionUnresolved();
        }

        return throwError(() => error);
      })
    );
  }

  /**
   * True when a failure means the credentials were rejected, rather than not reached.
   * One classifier, shared with the refresh coordinator, so init() and the coordinator can never
   * disagree about which failures end a session.
   */
  private isTerminalAuthError(error: unknown): boolean {
    return isTerminalAuthFailure(error);
  }

  /**
   * Register new user
   * Auto-login after registration
   */
  register(request: RegisterRequest): Observable<LoginResponse> {
    return this.apiAuthService.register(request).pipe(
      switchMap((loginResponse) => {
        // Update current user from login response
        this.currentUserSubject.next(loginResponse.user);

        // Call Init API to get marinas and roles
        return this.init().pipe(
          switchMap((initResponse) => {
            // For client users: use customerMarinaId from InitResponse
            this.applyPortalCustomerMarinaFromInit(initResponse);
            if (!this.shouldPreferPortalCustomerMarina(initResponse) && initResponse.accessibleMarinas.length > 0) {
              // For staff/admin users: Set primary marina as current if available
              const primaryMarina = initResponse.accessibleMarinas.find((m) => m.isPrimary);
              if (primaryMarina) {
                this.setCurrentMarina(primaryMarina.id);
              } else {
                this.setCurrentMarina(initResponse.accessibleMarinas[0].id);
              }
            }

            return of(loginResponse);
          })
        );
      }),
      catchError((error) => {
        this.clearAuth();
        return throwError(() => error);
      })
    );
  }

  /**
   * Logout user
   * Clears httpOnly cookies on the backend and clears cached data.
   * Always clears frontend auth state and redirects, even if API call fails.
   */
  logout(): Observable<void> {
    // Clear frontend auth state immediately
    this.clearAuth();
    
    // Try to call logout API, but don't block on it
    return this.apiAuthService.logout().pipe(
      tap(() => {
        console.log('[AuthService] Logout: Backend logout successful');
      }),
      catchError((error) => {
        // Log error but don't throw - we've already cleared frontend state
        console.warn('[AuthService] Logout: Backend logout failed, but frontend state cleared', error);
        // Return success observable so caller can proceed with redirect
        return of(void 0);
      })
    );
  }

  /**
   * Refresh authentication token
   * Refresh token is read from httpOnly cookie.
   * New tokens are set as httpOnly cookies by the backend.
   */
  refreshToken(): Observable<void> {
    // Kept for callers that want a refresh without knowing about coordination. Everything inside the
    // auth layer goes through the coordinator instead, so that concurrent callers share one call.
    return this.coordinator.getOrStart('interceptor', this.sessionState.sessionId);
  }

  /**
   * The refresh call itself. Registered with the coordinator; not called directly.
   *
   * Two things this deliberately does **not** do. It does not clear auth state on failure: a dropped
   * connection during a background refresh used to set `currentUser` to null, which stopped the
   * refresh timer and made every guard treat the user as logged out while their cookies were still
   * perfectly valid. And it does not write the new expirations blindly — the response is checked
   * against the session it was issued for first, so a refresh belonging to a login the user has since
   * replaced cannot overwrite the new one's bookkeeping.
   */
  private performRefresh(): Observable<void> {
    const sessionId = this.sessionState.sessionId;

    return this.apiAuthService.refreshToken().pipe(
      tap((refreshResponse) => {
        if (sessionId && !this.sessionState.isCurrent(sessionId)) {
          console.warn('[AuthService] Refresh: response belongs to a replaced session; discarding');
          return;
        }

        if (refreshResponse.accessTokenExpiresAt) {
          this.accessTokenExpiresAt = new Date(refreshResponse.accessTokenExpiresAt);
          this.saveTokenExpiration('accessTokenExpiresAt', this.accessTokenExpiresAt);
          this.recordAccessTokenIssuedAt();
        }
        if (refreshResponse.refreshTokenExpiresAt) {
          this.refreshTokenExpiresAt = new Date(refreshResponse.refreshTokenExpiresAt);
          this.saveTokenExpiration('refreshTokenExpiresAt', this.refreshTokenExpiresAt);
        }
        this.applySessionPolicy(refreshResponse);

        // Bumping the revision is what lets every other tab, and every request that 401'd while this
        // was in flight, know that a retry is enough and a second rotation is not needed.
        this.sessionState.recordCredentialChange(sessionId, {
          accessTokenExpiresAt: this.accessTokenExpiresAt?.toISOString() ?? null,
          refreshTokenExpiresAt: this.refreshTokenExpiresAt?.toISOString() ?? null,
          accessTokenIssuedAt: this.accessTokenIssuedAt?.toISOString() ?? null,
        });

        this.authStateSubject.next('authenticated');
        this.notifyTokenRefresh();
      }),
      map(() => undefined as void)
    );
  }

  /**
   * Begin a new login: a fresh identity for every later check to compare against, and a clean slate
   * for the coordinator and terminator (a new session must not inherit the old one's backoff or its
   * "already ending" flag).
   */
  private beginSession(response: { sessionId?: string | null } | null | undefined): void {
    this.coordinator.reset();
    this.terminator.reset();
    this.userActivity.start();
    // A login is the one request that is unambiguously the user's doing. Recording it means the idle
    // timer starts from now rather than from whatever the previous login left in shared storage —
    // which could be hours old, and would otherwise expire the new session the moment it is armed.
    this.userActivity.noteInput();
    this.sessionState.startSession(response?.sessionId ?? null, {
      accessTokenExpiresAt: this.accessTokenExpiresAt?.toISOString() ?? null,
      refreshTokenExpiresAt: this.refreshTokenExpiresAt?.toISOString() ?? null,
      accessTokenIssuedAt: this.accessTokenIssuedAt?.toISOString() ?? null,
    });
    this.authStateSubject.next('authenticated');
  }

  /**
   * A confirmed session with no identity yet gets one.
   *
   * Every path that establishes cookies calls beginSession(), but a session can also be *inherited*:
   * a browser logged in before this code shipped has valid cookies and no `marina_auth_state`. For
   * it, every identity comparison would see null — the terminator could not de-duplicate, other
   * tabs could not adopt its refreshes — until the next login. init() succeeding is the server
   * confirming the session, so it is the right moment to start counting.
   */
  private ensureSessionIdentity(response: { sessionId?: string | null } | null | undefined): void {
    if (this.sessionState.sessionId) {
      return;
    }
    console.log('[AuthService] Init: session confirmed but has no client identity; starting one');
    this.beginSession(response);
  }

  /**
   * Get current user info
   */
  getCurrentUser(): Observable<UserDto> {
    return this.apiAuthService.getCurrentUser().pipe(
      tap((user) => {
        this.currentUserSubject.next(user);
      }),
      catchError((error) => {
        // If getting user fails, clear auth
        this.clearAuth();
        return throwError(() => error);
      })
    );
  }

  /**
   * Update current user's display name in auth state and cached init data.
   * Useful when customer profile first/last name changes and UI depends on user.name.
   */
  syncCurrentUserDisplayName(firstName?: string | null, lastName?: string | null): void {
    const normalizedFirst = (firstName || '').trim();
    const normalizedLast = (lastName || '').trim();
    const fullName = `${normalizedFirst} ${normalizedLast}`.trim();
    if (!fullName) return;

    const currentUser = this.currentUserSubject.value;
    if (!currentUser) return;

    const updatedUser: UserDto = {
      ...currentUser,
      firstName: normalizedFirst,
      lastName: normalizedLast,
      name: fullName,
    };
    this.currentUserSubject.next(updatedUser);

    const currentInit = this.initDataSubject.value;
    if (currentInit?.user) {
      const updatedInit: InitResponse = { ...currentInit, user: updatedUser };
      this.initDataSubject.next(updatedInit);
      try {
        localStorage.setItem('marina_init_data', JSON.stringify(updatedInit));
      } catch {
        // Ignore storage errors
      }
    }
  }

  /**
   * Get accessible marinas from cached Init API response
   */
  getAccessibleMarinas(): MarinaInfo[] {
    const initData = this.initDataSubject.value;
    if (!initData) {
      return [];
    }
    return initData.accessibleMarinas;
  }

  /**
   * Get roles for a specific marina from cached Init API response
   */
  getRolesForMarina(marinaId: string): string[] {
    const initData = this.initDataSubject.value;
    if (!initData?.rolesPerMarina) {
      return [];
    }
    const map = initData.rolesPerMarina;
    const direct = map[marinaId];
    if (direct?.length) {
      return direct;
    }
    const key = Object.keys(map).find(
      (k) => k.toLowerCase() === marinaId.toLowerCase()
    );
    return key ? map[key] ?? [] : [];
  }

  /**
   * First admin-portal route after login / marina switch (staff roles only).
   * Keeps deck hands off /dashboard when rolesPerMarina keys or ordering would otherwise mis-route.
   */
  getStaffPortalHomePath(): string {
    const marinaId = this.getCurrentMarinaId();
    if (!marinaId) {
      return '/dashboard';
    }
    const roles = this.getRolesForMarina(marinaId);
    const has = (role: string) =>
      roles.some((r) => r.toUpperCase() === role.toUpperCase());
    if (has('ADMIN')) {
      return '/dashboard';
    }
    if (has('OFFICE_ADMIN')) {
      return '/billing';
    }
    if (has('DOCK_MASTER')) {
      return '/launch-board';
    }
    if (has('SERVICE_MANAGER')) {
      return '/dashboard';
    }
    if (has('SERVICE_TECH')) {
      return '/inventory';
    }
    if (has('DECK_HAND')) {
      return '/launch-board';
    }
    const user = this.currentUserSubject.value;
    // StaffRole.DECK_HAND = 5 — display field fallback if roles list failed to map
    if (user?.staffRole === 5) {
      return '/launch-board';
    }
    return '/dashboard';
  }

  /**
   * Set current marina.
   * Writes to a user-scoped localStorage key (§2.4 — prevents cross-user marina leakage).
   * Falls back to the legacy global key only when userId is not yet available.
   */
  setCurrentMarina(marinaId: string): void {
    // Update BehaviorSubject first (immediate availability)
    this.currentMarinaIdSubject.next(marinaId);
    try {
      const key = this.getMarinaStorageKey();
      localStorage.setItem(key, marinaId);
      console.log('[AuthService] setCurrentMarina:', marinaId, '(key:', key, ')');
    } catch (e) {
      console.warn('[AuthService] setCurrentMarina: Storage access failed', e);
    }
  }

  /**
   * Get current marina ID.
   *
   * §2.4 — Returns null when the user is not authenticated. This prevents the
   * HTTP interceptor and BaseApiService from attaching X-Marina-Id to requests
   * made on the login screen or before a session is established.
   *
   * Falls back to localStorage (user-scoped key) only when authenticated and the
   * in-memory BehaviorSubject is empty — e.g. a hard page-reload after login.
   */
  getCurrentMarinaId(): string | null {
    // §2.4 — No marina for unauthenticated callers.
    if (!this.isAuthenticated()) {
      return null;
    }

    // Memory-first: most up-to-date value.
    const subjectValue = this.currentMarinaIdSubject.value;
    if (subjectValue) {
      return subjectValue;
    }

    // Fallback: user-scoped storage (populated by initializeFromCache on page reload).
    try {
      const stored = this.getStoredMarinaId();
      if (stored) {
        this.currentMarinaIdSubject.next(stored);
        return stored;
      }
    } catch (e) {
      console.warn('[AuthService] getCurrentMarinaId: Storage access failed', e);
    }

    return null;
  }

  /**
   * Get current customer ID (for client users only).
   * Used e.g. to subscribe to SignalR payment updates on the billing dashboard.
   */
  getCustomerId(): string | null {
    const initData = this.initDataSubject.value;
    return initData?.customerId ?? null;
  }

  /**
   * Whether the user needs to select a marina (multiple marinas, none selected).
   * Used to show marina selection dialog after login.
   * Note: This should return false if a marina is being restored from storage or auto-selected.
   * We always auto-select a marina, so this should never return true automatically.
   */
  needsMarinaSelection(): boolean {
    const initData = this.initDataSubject.value;
    if (!initData || !this.currentUserSubject.value) return false;
    const marinas = initData.accessibleMarinas;
    const currentMarinaId = this.getCurrentMarinaId();
    
    // If there's a current marina, no selection needed
    if (currentMarinaId) {
      // Validate that the current marina is still accessible
      const isAccessible = marinas.length === 0 || marinas.some((m) => m.id === currentMarinaId);
      if (isAccessible) {
        return false;
      }
      // Marina is not accessible, but don't show modal - auto-select will handle it
      return false;
    }
    
    // No marina selected - but we always auto-select, so return false
    // The modal should only be shown when user explicitly requests it (e.g., clicking "Switch Marina")
    // Auto-selection happens in applyMarinaSelectionForStaff() and init() methods
    return false;
  }

  /**
   * Login API returns HTTP 200 for wrong-password attempts with failure metadata instead of 401.
   */
  private isFailedCredentialLogin(response: LoginResponse): boolean {
    return (
      !!response.isLockedOut ||
      response.failedAttemptsRemaining != null ||
      (!!response.errorMessage && !response.requiresTwoFactor)
    );
  }

  /**
   * True when the login response indicates auth cookies / tokens were issued.
   */
  private didLoginEstablishSession(response: LoginResponse): boolean {
    if (this.isFailedCredentialLogin(response)) {
      return false;
    }

    return !!(
      response.requiresTwoFactor ||
      response.accessTokenExpiresAt ||
      response.refreshTokenExpiresAt ||
      response.accessToken
    );
  }

  /**
   * Prefer CustomerMarinaId when Init resolved a linked portal customer (pure client or hybrid Client-scope).
   */
  private shouldPreferPortalCustomerMarina(init: InitResponse): boolean {
    return !!(
      init.customerMarinaId &&
      init.customerId &&
      (init.accessibleMarinas.length === 0 || init.hasLinkedPortalCustomer === true)
    );
  }

  private applyPortalCustomerMarinaFromInit(init: InitResponse): void {
    if (!this.shouldPreferPortalCustomerMarina(init)) return;
    const marinaId = init.customerMarinaId!;
    if (this.getCurrentMarinaId() !== marinaId) {
      console.log('[AuthService] Applying portal customer marina context:', marinaId);
      this.setCurrentMarina(marinaId);
    }
  }

  /**
   * Check if user is system admin
   * Uses cached Init API data (NOT JWT claim)
   */
  isSystemAdmin(): boolean {
    const initData = this.initDataSubject.value;
    return initData?.isSystemAdmin ?? false;
  }

  /**
   * Apply marina selection for staff/admin users.
   * - Single marina: auto-select
   * - Multiple marinas: use cached if valid, otherwise auto-select first/primary marina
   */
  private applyMarinaSelectionForStaff(initResponse: InitResponse): void {
    const marinas = initResponse.accessibleMarinas;
    if (marinas.length === 0) return;

    if (marinas.length === 1) {
      this.setCurrentMarina(marinas[0].id);
      console.log('[AuthService] Single marina: auto-selected', marinas[0].name);
      return;
    }

    // Multiple marinas: use validated cached preference, otherwise auto-select first/primary.
    try {
      const cachedMarinaId = this.getStoredMarinaId();
      if (cachedMarinaId && marinas.some((m) => m.id === cachedMarinaId)) {
        this.setCurrentMarina(cachedMarinaId);
        console.log('[AuthService] Multiple marinas: restored from user-scoped cache');
        return;
      }
    } catch (e) {
      console.warn('[AuthService] applyMarinaSelectionForStaff: Storage access failed, will auto-select', e);
    }

    // No valid cached marina: auto-select first marina (or primary if available)
    const primaryMarina = marinas.find((m) => m.isPrimary);
    const marinaToSelect = primaryMarina || marinas[0];
    this.setCurrentMarina(marinaToSelect.id);
    console.log('[AuthService] Multiple marinas: auto-selected first marina', marinaToSelect.name);
  }

  // ---------------------------------------------------------------------------
  // Storage key helpers (§2.4 — user-scoped marina preference)
  // ---------------------------------------------------------------------------

  /**
   * Returns the user-scoped localStorage key for the current user.
   * Falls back to the legacy global key when the user identity is not yet known
   * (e.g. during initial setup — should not normally be reached after §2.4 fixes).
   */
  private getMarinaStorageKey(): string {
    const userId =
      this.currentUserSubject.value?.id ?? this.initDataSubject.value?.user?.id;
    return userId ? `marina_current_marina_id:${userId}` : 'marina_current_marina_id';
  }

  /**
   * Reads the stored marina preference for the given user.
   * Tries the user-scoped key first; migrates from the legacy global key if found.
   */
  private getStoredMarinaId(userId?: string): string | null {
    const resolvedUserId =
      userId ?? this.currentUserSubject.value?.id ?? this.initDataSubject.value?.user?.id;

    if (!resolvedUserId) {
      return null;
    }

    try {
      const userKey = `marina_current_marina_id:${resolvedUserId}`;
      const userScoped = localStorage.getItem(userKey);
      if (userScoped) return userScoped;

      // One-time migration: if the legacy global key exists, adopt it under the user-scoped key.
      const legacy = localStorage.getItem('marina_current_marina_id');
      if (legacy) {
        localStorage.setItem(userKey, legacy);
        localStorage.removeItem('marina_current_marina_id');
        console.log('[AuthService] Migrated marina key to user-scoped:', userKey);
        return legacy;
      }
    } catch (e) {
      console.warn('[AuthService] getStoredMarinaId: Storage access failed', e);
    }

    return null;
  }

  /**
   * Removes the user-scoped marina preference from storage.
   * Called when the stored marina is no longer accessible.
   */
  private clearStoredMarina(userId?: string): void {
    const resolvedUserId =
      userId ?? this.currentUserSubject.value?.id ?? this.initDataSubject.value?.user?.id;
    if (!resolvedUserId) return;
    try {
      localStorage.removeItem(`marina_current_marina_id:${resolvedUserId}`);
    } catch (e) {
      // Ignore storage errors
    }
  }

  // ---------------------------------------------------------------------------

  /**
   * Save token expiration time to localStorage
   */
  private saveTokenExpiration(key: 'accessTokenExpiresAt' | 'refreshTokenExpiresAt' | 'accessTokenIssuedAt', expiresAt: Date): void {
    try {
      localStorage.setItem(key, expiresAt.toISOString());
    } catch (e) {
      console.warn('[AuthService] Failed to save token expiration to localStorage', e);
    }
  }

  /**
   * Record the current time as the access-token issued-at timestamp.
   * Called immediately after receiving a new access token so TokenRefreshTimerService
   * can compute the full token lifetime for accurate proactive refresh scheduling.
   */
  private recordAccessTokenIssuedAt(): void {
    this.accessTokenIssuedAt = new Date();
    this.saveTokenExpiration('accessTokenIssuedAt', this.accessTokenIssuedAt);
  }

  /**
   * Restore token expiration times from localStorage
   * IMPORTANT: We restore expired access tokens too if refresh token is still valid
   * This allows the interceptor to detect expired access tokens and refresh them
   */
  private restoreTokenExpirations(): void {
    this.restoreSessionPolicy();
    try {
      // First, restore refresh token expiration to check if it's still valid
      const refreshTokenExpiresAtStr = localStorage.getItem('refreshTokenExpiresAt');
      let refreshTokenValid = false;
      if (refreshTokenExpiresAtStr) {
        const refreshExpiresAt = new Date(refreshTokenExpiresAtStr);
        const now = new Date();
        if (refreshExpiresAt > now) {
          this.refreshTokenExpiresAt = refreshExpiresAt;
          refreshTokenValid = true;
          const timeUntilExpiration = refreshExpiresAt.getTime() - now.getTime();
          console.log('[AuthService] Restored refresh token expiration from localStorage:', {
            expiresAt: this.refreshTokenExpiresAt.toISOString(),
            timeUntilExpiration: `${Math.floor(timeUntilExpiration / 1000)}s`,
            isExpired: false,
          });
        } else {
          // Refresh token expired, remove from storage
          console.log('[AuthService] Refresh token expiration in localStorage is expired, removing:', {
            expiresAt: refreshExpiresAt.toISOString(),
            now: now.toISOString(),
            timeSinceExpiration: `${Math.floor((now.getTime() - refreshExpiresAt.getTime()) / 1000)}s`,
          });
          localStorage.removeItem('refreshTokenExpiresAt');
        }
      } else {
        console.log('[AuthService] No refresh token expiration found in localStorage');
      }

      // Restore access-token issued-at (used by timer to derive full token lifetime)
      const accessTokenIssuedAtStr = localStorage.getItem('accessTokenIssuedAt');
      if (accessTokenIssuedAtStr) {
        const issuedAt = new Date(accessTokenIssuedAtStr);
        // Only restore if it's in the past (sanity check)
        if (issuedAt.getTime() <= Date.now()) {
          this.accessTokenIssuedAt = issuedAt;
        }
      }

      // Now restore access token expiration
      // CRITICAL: Restore even if expired, as long as refresh token is valid
      // This allows interceptor to detect expired access token and refresh it
      const accessTokenExpiresAtStr = localStorage.getItem('accessTokenExpiresAt');
      if (accessTokenExpiresAtStr) {
        const expiresAt = new Date(accessTokenExpiresAtStr);
        const now = new Date();
        const isExpired = expiresAt <= now;
        
        if (isExpired && refreshTokenValid) {
          // Access token expired but refresh token valid - KEEP the expiration time
          // This allows interceptor to detect and refresh
          this.accessTokenExpiresAt = expiresAt;
          const timeSinceExpiration = now.getTime() - expiresAt.getTime();
          console.log('[AuthService] Restored expired access token expiration (refresh token still valid):', {
            expiresAt: this.accessTokenExpiresAt.toISOString(),
            now: now.toISOString(),
            timeSinceExpiration: `${Math.floor(timeSinceExpiration / 1000)}s`,
            willTriggerRefresh: true,
          });
        } else if (!isExpired) {
          // Access token still valid
          this.accessTokenExpiresAt = expiresAt;
          const timeUntilExpiration = expiresAt.getTime() - now.getTime();
          console.log('[AuthService] Restored access token expiration from localStorage:', {
            expiresAt: this.accessTokenExpiresAt.toISOString(),
            timeUntilExpiration: `${Math.floor(timeUntilExpiration / 1000)}s`,
            isExpired: false,
          });
        } else {
          // Both tokens expired, remove from storage
          console.log('[AuthService] Access token expiration expired and refresh token also expired, removing:', {
            expiresAt: expiresAt.toISOString(),
            now: now.toISOString(),
            timeSinceExpiration: `${Math.floor((now.getTime() - expiresAt.getTime()) / 1000)}s`,
          });
          localStorage.removeItem('accessTokenExpiresAt');
        }
      } else {
        console.log('[AuthService] No access token expiration found in localStorage');
      }
    } catch (e) {
      console.warn('[AuthService] Failed to restore token expirations from localStorage', e);
    }
  }

  /**
   * Initialize from cached data (localStorage for init and marina — shared across tabs).
   *
   * §2.4 — Marina is ONLY restored here, after we have confirmed a valid authenticated
   * context from the cached initData. This ensures the marina is never treated as
   * "current" while unauthenticated (e.g. login screen, password-reset page).
   *
   * The stored marina is validated against the user's accessible marinas list before
   * being applied; invalid/inaccessible stored values are cleared and auto-selection
   * is used instead.
   */
  /**
   * Returns true when a prior authenticated session was found in cache (and the
   * caller should therefore also restore token expiration data). Returns false on
   * unauthenticated startup (login screen, 2FA page, password-reset, etc.) so that
   * unrelated localStorage reads are completely skipped on those routes.
   */
  private initializeFromCache(): boolean {
    try {
      // Invitation / password-setup / reset links must start clean — restoring a prior
      // session here makes isAuthenticated() true and can bounce the deep link to Login.
      if (isPublicAuthFlowUrl()) {
        console.log('[AuthService] InitializeFromCache: Skipping on public auth flow');
        return false;
      }

      // Restore auth context from localStorage (shared across tabs).
      // Marina is intentionally NOT restored before this point (§2.4).
      const cachedInitData = localStorage.getItem('marina_init_data');
      if (!cachedInitData) {
        console.log('[AuthService] InitializeFromCache: No cached initData - starting unauthenticated');
        return false;
      }

      const initData: InitResponse = JSON.parse(cachedInitData);
      this.initDataSubject.next(initData);
      this.currentUserSubject.next(initData.user);
      console.log('[AuthService] InitializeFromCache: Restored initData for user', initData.user?.id);

      // Now that we know who the user is, read the user-scoped marina preference.
      // Only trust it if it is still in the user's accessible marina list (§2.4).
      const userId = initData.user?.id;
      const storedMarinaId = userId ? this.getStoredMarinaId(userId) : null;

      if (storedMarinaId) {
        const treatsMarinaAsPortalCustomer =
          initData.accessibleMarinas.length === 0 ||
          (initData.hasLinkedPortalCustomer === true &&
            !!initData.customerMarinaId &&
            !!initData.customerId);
        const isValid = treatsMarinaAsPortalCustomer
          ? storedMarinaId === initData.customerMarinaId
          : initData.accessibleMarinas.some((m) => m.id === storedMarinaId);

        if (isValid) {
          this.currentMarinaIdSubject.next(storedMarinaId);
          console.log('[AuthService] InitializeFromCache: Validated and restored marina', storedMarinaId);
          return true;
        } else {
          // Stored marina is no longer accessible — clear it and fall through to auto-select.
          console.warn('[AuthService] InitializeFromCache: Stored marina is not accessible, clearing:', storedMarinaId);
          this.clearStoredMarina(userId);
        }
      }

      // No valid stored marina — apply auto-selection logic.
      if (this.shouldPreferPortalCustomerMarina(initData)) {
        this.setCurrentMarina(initData.customerMarinaId!);
      } else if (initData.accessibleMarinas.length > 0) {
        this.applyMarinaSelectionForStaff(initData);
      }
      return true;
    } catch (e) {
      console.warn('[AuthService] InitializeFromCache: Error restoring from cache', e);
      return false;
    }
  }

  /**
   * Request password reset
   * Sends password reset email to the user
   * @param email User's email address
   * @param portalType "customer" or "admin" - determines which reset password URL to use
   */
  requestPasswordReset(email: string, portalType: 'customer' | 'admin' = 'customer'): Observable<void> {
    const request: RequestPasswordResetRequest = { email, portalType };
    return this.apiAuthService.requestPasswordReset(request);
  }

  /**
   * Confirm password reset
   * Resets password using token from email
   */
  confirmPasswordReset(email: string, token: string, newPassword: string): Observable<void> {
    const request: ConfirmPasswordResetRequest = {
      email,
      token,
      newPassword,
    };
    return this.apiAuthService.confirmPasswordReset(request);
  }

  /**
   * Change password
   * Changes user's password (authenticated)
   */
  changePassword(currentPassword: string, newPassword: string): Observable<void> {
    const currentUser = this.currentUserValue;
    if (!currentUser) {
      return throwError(() => new Error('User not authenticated'));
    }

    const request: ChangePasswordRequest = {
      currentPassword,
      newPassword,
    };
    return this.apiAuthService.changePassword(request);
  }

  /**
   * Send activation link
   * Sends account activation email with secure link (24-hour validity)
   */
  sendActivationLink(email: string): Observable<void> {
    const request: SendActivationLinkRequest = { email };
    return this.apiAuthService.sendActivationLink(request);
  }

  /**
   * Activate account
   * Activates account with token and sets password, then auto-logs in user
   */
  activateAccount(email: string, token: string, password: string): Observable<LoginResponse> {
    // Clear all cache and storage before activation to ensure clean state
    // This prevents stale data (marina IDs, tokens, etc.) from interfering with activation
    console.log('[AuthService] ActivateAccount: Clearing cache and storage before activation');
    this.currentUserSubject.next(null);
    this.initDataSubject.next(null);
    this.currentMarinaIdSubject.next(null);
    this.accessTokenExpiresAt = null;
    this.refreshTokenExpiresAt = null;
    this.accessTokenIssuedAt = null;
    
    try {
      // Clear all auth-related localStorage items
      localStorage.removeItem('marina_init_data');
      localStorage.removeItem('marina_current_marina_id');
      localStorage.removeItem('accessTokenExpiresAt');
      localStorage.removeItem('refreshTokenExpiresAt');
      localStorage.removeItem('accessTokenIssuedAt');
      console.log('[AuthService] ActivateAccount: Cleared all localStorage items');
    } catch (e) {
      console.warn('[AuthService] ActivateAccount: Failed to clear localStorage', e);
    }
    
    const request: ActivateAccountRequest = { email, token, password };
    return this.apiAuthService.activateAccount(request).pipe(
      tap((loginResponse) => {
        // Store token expiration times (from API response - enables proactive refresh)
        if (loginResponse.accessTokenExpiresAt) {
          this.accessTokenExpiresAt = new Date(loginResponse.accessTokenExpiresAt);
          this.saveTokenExpiration('accessTokenExpiresAt', this.accessTokenExpiresAt);
          this.recordAccessTokenIssuedAt();
        }
        if (loginResponse.refreshTokenExpiresAt) {
          this.refreshTokenExpiresAt = new Date(loginResponse.refreshTokenExpiresAt);
          this.saveTokenExpiration('refreshTokenExpiresAt', this.refreshTokenExpiresAt);
        }
        this.applySessionPolicy(loginResponse);
      }),
      switchMap((loginResponse) => {
        // If 2FA is required, return the response immediately without calling init()
        // The init() call will happen after 2FA verification is complete
        if (loginResponse.requiresTwoFactor && loginResponse.twoFactorSessionId) {
          console.log('[AuthService] ActivateAccount: 2FA required, skipping init()');
          return of(loginResponse);
        }
        
        // No 2FA required - call Init API (init() handles marina selection).
        // If init fails (e.g. cookie/session propagation race in incognito/new device),
        // still treat activation as successful so user can continue by logging in manually.
        // Password has already been set server-side by activateAccount().
        return this.init().pipe(
          switchMap(() => of(loginResponse)),
          catchError((initError) => {
            console.warn('[AuthService] ActivateAccount: init() failed after successful activation, continuing without auto-login', initError);
            return of(loginResponse);
          })
        );
      }),
      catchError((error) => {
        console.error('[AuthService] ActivateAccount: Error occurred', error);
        this.clearAuth();
        return throwError(() => error);
      })
    );
  }

  /**
   * Admin reset password
   * Admin initiates password reset for a user
   */
  adminResetPassword(userId: string): Observable<void> {
    const request: AdminResetPasswordRequest = { userId };
    return this.apiAuthService.adminResetPassword(request);
  }

  /**
   * Setup storage event listener to detect token refreshes from other tabs
   * When another tab refreshes tokens, this tab will update its expiration times
   */
  private setupStorageEventListener(): void {
    if (typeof window === 'undefined') {
      return; // Not in browser environment
    }

    window.addEventListener('storage', (event: StorageEvent) => {
      // Only handle changes to token expiration keys
      if (event.key !== 'accessTokenExpiresAt' && event.key !== 'refreshTokenExpiresAt' && event.key !== 'accessTokenIssuedAt') {
        return;
      }

      console.log('[AuthService] Storage event detected - token data updated in another tab:', {
        key: event.key,
        oldValue: event.oldValue,
        newValue: event.newValue,
      });

      if (event.key === 'accessTokenIssuedAt' && event.newValue) {
        const issuedAt = new Date(event.newValue);
        if (issuedAt.getTime() <= Date.now()) {
          this.accessTokenIssuedAt = issuedAt;
        }
      } else if (event.key === 'accessTokenExpiresAt' && event.newValue) {
        const newExpiresAt = new Date(event.newValue);
        if (newExpiresAt > new Date()) {
          this.accessTokenExpiresAt = newExpiresAt;
          console.log('[AuthService] Updated access token expiration from other tab:', this.accessTokenExpiresAt);
          // Notify TokenRefreshTimerService to restart timer with new expiration
          this.notifyTokenRefresh();
        }
      } else if (event.key === 'refreshTokenExpiresAt' && event.newValue) {
        const newExpiresAt = new Date(event.newValue);
        if (newExpiresAt > new Date()) {
          this.refreshTokenExpiresAt = newExpiresAt;
          console.log('[AuthService] Updated refresh token expiration from other tab:', this.refreshTokenExpiresAt);
        }
        // No `else` here on purpose. This used to clear auth when another tab wrote an expiry in the
        // past, which is a timestamp comparison against this tab's clock — not evidence that the
        // session ended. A tab ending a session now says so explicitly, over BroadcastChannel, and
        // the terminator acts on that.
      }
    });
  }

  /**
   * Notify that token was refreshed (for TokenRefreshTimerService to restart timer)
   * This is called when we detect a token refresh from another tab
   */
  private notifyTokenRefresh(): void {
    // Dispatch a custom event that TokenRefreshTimerService can listen to
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('tokenRefreshed', {
        detail: { accessTokenExpiresAt: this.accessTokenExpiresAt }
      }));
    }
  }

  /**
   * Clear all authentication data.
   *
   * §2.4 — The user-scoped marina preference is intentionally kept in localStorage
   * so it can be restored (and re-validated) on the user's next login. The in-memory
   * BehaviorSubject is cleared so getCurrentMarinaId() returns null immediately
   * (interceptor stops sending X-Marina-Id).
   *
   * The legacy global key (marina_current_marina_id) is also removed on logout to
   * prevent cross-user leakage on shared browsers.
   */
  private clearAuth(): void {
    this.userActivity.stop();
    this.authStateSubject.next('anonymous');
    this.coordinator.reset();
    this.currentUserSubject.next(null);
    this.initDataSubject.next(null);
    // §2.4 — Clear in-memory marina so interceptor stops injecting X-Marina-Id.
    this.currentMarinaIdSubject.next(null);
    this.accessTokenExpiresAt = null;
    this.refreshTokenExpiresAt = null;
    this.accessTokenIssuedAt = null;
    this.sessionPolicySubject.next(null);
    try {
      localStorage.removeItem('sessionPolicy');
      localStorage.removeItem('marina_init_data');
      localStorage.removeItem('accessTokenExpiresAt');
      localStorage.removeItem('refreshTokenExpiresAt');
      localStorage.removeItem('accessTokenIssuedAt');
      // Remove the legacy unsecured global key (§2.4 — user-scoped key is kept for preference restore).
      localStorage.removeItem('marina_current_marina_id');
      // Note: the user-scoped key (marina_current_marina_id:{userId}) is deliberately kept
      // so the marina preference can be restored and re-validated on next login.
    } catch (e) {
      // Ignore storage errors
    }
  }
}

